import { useCallback, useState } from 'react';
import { addInsight, archiveInsight, listInsights } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

/**
 * Account insights — short, curated facts the AI assistant reads before answering
 * (e.g. "re-uploading all statements through July 2026", "Jan–Apr 2025 Ambetter
 * came from a customer-reconstructed workbook"). WRITE FOR THE CUSTOMER'S EYES:
 * the assistant may quote a note verbatim in an answer. Max 20 active; archive
 * anything stale — wrong context is worse than none.
 */
export function InsightsPanel({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => listInsights(accountId), [accountId]),
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = note.trim();
    if (trimmed.length < 3 || busy) return;
    setBusy(true);
    setFormError(null);
    try {
      await addInsight(accountId, trimmed);
      setNote('');
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id: string) => {
    setBusy(true);
    try {
      await archiveInsight(accountId, id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load insights</span>;

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
        AI assistant insights
      </div>
      <p className="mb-2 text-xs text-neutral-400">
        Facts the assistant reads before answering this account’s questions. Written for the
        customer’s eyes — it may quote them verbatim. Coverage warnings (missing / partial statement
        months) are added automatically; only add what the data can’t reveal.
      </p>
      {!data ? (
        <span className="text-xs text-neutral-400">Loading…</span>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {data.data.length === 0 && (
            <li className="py-1.5 text-xs text-neutral-400">No insights yet.</li>
          )}
          {data.data.map((i) => (
            <li key={i.id} className="flex items-start justify-between gap-3 py-1.5">
              <div>
                <p className="text-sm text-neutral-800">{i.note}</p>
                <p className="text-[10px] text-neutral-400">
                  {i.createdBy ?? i.source} · {new Date(i.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => archive(i.id)}
                className="shrink-0 rounded border border-neutral-200 px-2 py-0.5 text-[11px] text-neutral-500 transition hover:border-rose-300 hover:text-rose-600 disabled:opacity-50"
              >
                Archive
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex gap-2">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          maxLength={300}
          placeholder="Add a fact the assistant should know…"
          className="w-full rounded border border-neutral-300 px-2 py-1 text-sm outline-none focus:border-emerald-500"
        />
        <button
          type="button"
          disabled={busy || note.trim().length < 3}
          onClick={submit}
          className="rounded bg-emerald-600 px-3 py-1 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {formError && <p className="mt-1 text-xs text-rose-600">{formError}</p>}
    </div>
  );
}
